/**
 * What a pre-request / test script is allowed to see and change.
 *
 * COL-02 / COL-03. Scripts arrive verbatim inside imported Postman, Insomnia
 * and Bruno files and run automatically on Send, so "what goes into the
 * sandbox" and "what comes back out of it" are both attacker-facing surfaces:
 *
 *   - Going in, every scope (global + workspace + environment + collection)
 *     used to be flattened into one map with no regard for the `secret` flag
 *     the Environment editor's lock icon sets. A three-line imported script
 *     could read `STRIPE_SK` and stash it in a variable the *next* request
 *     interpolates into its URL.
 *   - Coming out, `environment.set()` used to be written straight into the
 *     user's selected environment, which persists and syncs to teammates — so
 *     `pm.environment.set('baseUrl', 'https://mitm.example')` permanently
 *     redirected every other collection in the workspace.
 *
 * Both RequestBuilder and collectionRunner build their script context here so
 * the two paths cannot drift apart again (they already had, which is how the
 * runner ended up correct about mutations and RequestBuilder did not).
 *
 * `secret: true` now has ONE meaning, everywhere: the value is for
 * substitution into a request the user sends, and for nothing else. It is not
 * handed to scripts, it is not writable by scripts, and (see
 * `yamlWorkspace/serialize.js`) it is not written into the git-mirrored YAML.
 *
 * Scripts that genuinely need a secret — request signing, HMAC headers — are
 * not left without a route: the variable carries an explicit per-variable
 * opt-in, `scriptAccess: true`, which the user sets on that one variable.
 * Default off, so the failure mode of forgetting is a script that cannot read
 * a secret rather than a secret that leaks.
 */
import { SCOPE_PRECEDENCE_LOWEST_FIRST } from './variablePrecedence';

/**
 * Is this variable withheld from scripts?
 *
 * `allSecretsWithheld` upgrades the policy from "secrets without a grant" to
 * "every secret, grant or not". It exists for the AI pre-request DRY RUN (see
 * `aiPreRequestDryRun.js`): `scriptAccess: true` means "the Send path may use
 * this value", not "a model-authored script may read it and the AI vendor may
 * see whatever it does with it". A dry run only has to answer "does this
 * compile and does it throw", which needs no real secret, so on that one path
 * no secret value enters the sandbox at all.
 */
export function isScriptWithheld(variable, { allSecretsWithheld = false } = {}) {
  if (!variable || variable.secret !== true) return false;
  if (allSecretsWithheld) return true;
  return variable.scriptAccess !== true;
}

/**
 * Every scope the app resolves `{{vars}}` from, lowest priority first — the
 * same order and precedence RequestBuilder's `substituteVariables` and the
 * runner's `makeSubstituteVariables` use, so a script sees the value the
 * request would have sent.
 */
function orderedScopes({
  globalVariables,
  workspaceVariables,
  environmentVariables,
  collectionVariables,
  folderVariables,
} = {}) {
  // Built from the one precedence list (utils/variablePrecedence), lowest
  // first. `folderVariables` arrives nearest-first and is reversed so the
  // NEAREST folder is written last and wins, above collection.
  const byScope = {
    global: globalVariables,
    workspace: workspaceVariables,
    environment: environmentVariables,
    collection: collectionVariables,
    folder: Array.isArray(folderVariables) ? [...folderVariables].reverse() : null,
  };
  return SCOPE_PRECEDENCE_LOWEST_FIRST.map((scope) => byScope[scope]);
}

/**
 * Flat `{ key: value }` snapshot for the sandbox, with withheld secrets left
 * out entirely — not blanked, not sentinel-valued. A key that is absent cannot
 * be read back by accident through some other accessor.
 *
 * `overlay` (see scriptEnvOverlay.js) is applied last so a value an earlier
 * script set in this session is what the next one reads — but a withheld key
 * is stripped AFTER the overlay too, so a value parked in the overlay by an
 * earlier, more permissive run cannot re-enter through the side door. That
 * matters for `allSecretsWithheld`: `SIGNING_KEY` is freely settable on the
 * Send path, so the overlay legitimately holds it, and the dry run must still
 * not see it.
 */
export function buildScriptVariableMap(scopes = {}, overlay = null, options = {}) {
  const map = {};
  const withheld = [];
  for (const source of orderedScopes(scopes)) {
    if (!Array.isArray(source)) continue;
    for (const v of source) {
      if (!v?.key) continue;
      if (v.enabled === false) continue;
      if (isScriptWithheld(v, options)) {
        withheld.push(v.key);
        delete map[v.key];
        continue;
      }
      map[v.key] = v.value ?? '';
    }
  }
  if (overlay && typeof overlay === 'object') {
    for (const [key, value] of Object.entries(overlay)) {
      if (value === null || value === undefined) delete map[key];
      else map[key] = value;
    }
  }
  for (const key of withheld) delete map[key];
  return map;
}

/**
 * Keys the sandbox must refuse rather than silently miss.
 *
 * Absence alone would be quiet and confusing: a signing script would compute
 * an HMAC over `null` and send a broken request. The sandbox is told which
 * keys exist-but-are-withheld so `get`/`set` on them throw a message naming
 * the opt-in, which surfaces as a script error the user actually sees.
 *
 * Withheld-ness is decided per key across all scopes: if ANY scope's entry for
 * that key is a withheld secret, the key is withheld — a lower-priority
 * non-secret entry must not unlock a higher-priority secret one.
 */
export function withheldSecretKeys(scopes = {}, options = {}) {
  const withheld = new Set();
  for (const source of orderedScopes(scopes)) {
    if (!Array.isArray(source)) continue;
    for (const v of source) {
      if (!v?.key) continue;
      if (isScriptWithheld(v, options)) withheld.add(v.key);
    }
  }
  return [...withheld];
}

/**
 * Filter a script's `environment.set()`/`unset()` results before anything acts
 * on them. A script may not create, overwrite or delete a withheld secret —
 * otherwise "scripts cannot read secrets" would be one `set()` + one read away
 * from false.
 *
 * Returns the accepted mutations plus the keys that were refused, so the
 * caller can tell the user instead of failing silently.
 */
export function filterScriptEnvMutations(envMutations, withheldKeys = []) {
  const blocked = new Set(withheldKeys.map(String));
  const accepted = {};
  const refused = [];
  for (const [key, value] of Object.entries(envMutations || {})) {
    if (blocked.has(String(key))) {
      refused.push(String(key));
      continue;
    }
    accepted[key] = value;
  }
  return { accepted, refused };
}
